import React from "react";
import { createPortal } from "react-dom";

export type ContextMenuItem = {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  onSelect: () => void;
  disabled?: boolean;
  itemClassName?: string;
};

export type ContextMenuProps = {
  anchor: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: ContextMenuItem[];
  /** Optional manual anchor point; if provided overrides anchor's rect. */
  anchorPoint?: { x: number; y: number } | null;
  isArabic?: boolean;
};

/**
 * Lightweight portal-based context menu. Positions itself next to the
 * referenced anchor or free-floating at anchorPoint (right-click flow).
 * Closes on outside click, ESC, scroll, or resize.
 */
export default function ContextMenu({
  anchor,
  open,
  onClose,
  items,
  anchorPoint = null,
  isArabic = false,
}: ContextMenuProps) {
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const place = () => {
      if (anchorPoint) {
        setPosition({ top: anchorPoint.y, left: anchorPoint.x });
        return;
      }
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 220;
      const left = isArabic
        ? Math.max(8, rect.right - menuWidth)
        : rect.left;
      setPosition({ top: rect.bottom + 4, left });
    };
    place();
    const onScroll = () => onClose();
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target as Node)) return;
      onClose();
    };
    setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, anchor, anchorPoint, onClose, isArabic]);

  if (!open || !position) return null;

  // Flip to keep the menu inside the viewport.
  const menuWidth = 220;
  const menuHeight = Math.min(items.length * 40 + 12, 400);
  const maxLeft = window.innerWidth - menuWidth - 8;
  const maxTop = window.innerHeight - menuHeight - 8;
  const left = Math.min(Math.max(8, position.left), Math.max(8, maxLeft));
  const top = Math.min(Math.max(8, position.top), Math.max(8, maxTop));

  const content = (
    <div
      ref={menuRef}
      role="menu"
      dir={isArabic ? "rtl" : "ltr"}
      className="fixed min-w-[220px] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-surface-elevated dark:text-slate-50 dark:shadow-modal-dark"
      style={{ top, left, zIndex: 1400 }}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) return;
            item.onSelect();
            onClose();
          }}
          className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors disabled:opacity-60 ${
            isArabic ? "flex-row-reverse text-right" : "text-start"
          } ${
            item.itemClassName ??
            (item.danger
              ? "text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
              : "text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-white/[0.06]")
          }`}
        >
          {item.icon ? <span className="h-4 w-4 shrink-0">{item.icon}</span> : null}
          <span className="truncate">{item.label}</span>
        </button>
      ))}
    </div>
  );

  return createPortal(content, document.body);
}
